import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuItem from '@mui/material/MenuItem';
import {
  Alert,
  Avatar,
  CardMedia,
  Modal,
  Snackbar,
  Stack,
  TextField,
  Divider,
  Grid,
  Chip,
  Tooltip,
} from '@mui/material';
import EvStationIcon from '@mui/icons-material/EvStation';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import BookIcon from '@mui/icons-material/Book';
import { keyframes } from '@mui/system';

import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { useFetch } from '../../hook/useFetch';
import { rutas } from '../../rutas/rutas';
import { exit, setInformacion } from '../../storage/slices/usuarios';

import { getCookie } from '../../utils/getCookie';
import axios from 'axios';

import logo from '../../Web Mundo/logo.png';
import hueso from '../../Web Mundo/hueso.png';
import discord from '../../Web Mundo/discord.png';

// =======================
// Modal animation & style
// =======================
const glowBorder = keyframes`
  0% {
    box-shadow: 0 0 10px rgba(255,215,0,0.4), 0 0 0 rgba(255,215,0,0.1);
  }
  50% {
    box-shadow: 0 0 22px rgba(255,215,0,0.9), 0 0 26px rgba(255,215,0,0.4);
  }
  100% {
    box-shadow: 0 0 10px rgba(255,215,0,0.4), 0 0 0 rgba(255,215,0,0.1);
  }
`;

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 300, sm: 420, md: 540 },
  maxHeight: '80vh',
  bgcolor: '#050710',
  borderRadius: 3,
  p: 2.2,
  color: 'white',
  overflowY: 'auto',
  border: '1px solid rgba(255,215,0,0.8)',
  animation: `${glowBorder} 4s ease-in-out infinite`,
};

const BURN_PATH = '/game/album?page=6';

const WITHDRAW_RULES = Object.freeze({
  HUESO: {
    minimum: 1000,
    dailyLimit: 50000,
    balanceField: 'dineroReal',
    step: '0.01',
  },
  VIPHUESO: {
    minimum: 1,
    dailyLimit: 200,
    balanceField: 'fusion',
    step: '0.001',
  },
});

// =======================
// Component
// =======================
const NavBar = () => {
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [open2, setOpen2] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [errorText, setErrorText] = React.useState('Error');
  const [password, setPassword] = React.useState('');
  const [deposit, setDeposit] = React.useState(0);
  const [successText, setSuccessText] = React.useState('Operation completed successfully.');
  const [withdrawLoading, setWithdrawLoading] = React.useState('');
  const [withdrawStatusLoading, setWithdrawStatusLoading] = React.useState(false);
  const [withdrawAmounts, setWithdrawAmounts] = React.useState({
    HUESO: '',
    VIPHUESO: '',
  });
  const [withdrawDaily, setWithdrawDaily] = React.useState({
    HUESO: {
      minimum: WITHDRAW_RULES.HUESO.minimum,
      limit: WITHDRAW_RULES.HUESO.dailyLimit,
      used: 0,
      remaining: WITHDRAW_RULES.HUESO.dailyLimit,
      timezone: 'UTC',
    },
    VIPHUESO: {
      minimum: WITHDRAW_RULES.VIPHUESO.minimum,
      limit: WITHDRAW_RULES.VIPHUESO.dailyLimit,
      used: 0,
      remaining: WITHDRAW_RULES.VIPHUESO.dailyLimit,
      timezone: 'UTC',
    },
  });

  // Poder Especial consumible. La fuente real siempre es /api/arena/status.
  const [poderEspecial, setPoderEspecial] = React.useState({
    activo: 0,
    original: 0,
    consumido: 0,
    loading: true,
    loaded: false,
    error: false,
  });
  const poderEspecialRequestRef = React.useRef(false);

  const dispatch = useDispatch();
  const [peticion] = useFetch();
  const { informacion } = useSelector((state) => state.userSlice);
  const informacionRef = React.useRef(informacion);

  React.useEffect(() => {
    informacionRef.current = informacion;
  }, [informacion]);

  // =======================
  // Actualizar usuario / saldos globales
  // =======================

  // ✅ Esta función ahora acepta varias formas de respuesta:
  // data.usuario
  // data.user
  // data.resultado.usuario
  // data.resultado.user
  // data.resultado  <--- ESTA era la que faltaba
  const extraerUsuarioRespuesta = (data) => {
    const posibleUsuario =
      data?.usuario ||
      data?.resultado?.usuario ||
      data?.resultado?.user ||
      data?.user ||
      data?.resultado ||
      null;

    if (!posibleUsuario || typeof posibleUsuario !== 'object') {
      return null;
    }

    // ✅ Validación para no meter cualquier objeto incorrecto en Redux
    const pareceUsuario =
      typeof posibleUsuario.dinero !== 'undefined' ||
      typeof posibleUsuario.dineroReal !== 'undefined' ||
      typeof posibleUsuario.fusion !== 'undefined' ||
      typeof posibleUsuario.energia !== 'undefined' ||
      typeof posibleUsuario.nombre !== 'undefined';

    if (!pareceUsuario) {
      return null;
    }

    return posibleUsuario;
  };

  // Updates Redux and the reference immediately so a later event cannot
  // merge the new balance with an old copy.
  const aplicarUsuarioLocal = React.useCallback(
    (usuarioNuevo) => {
      if (!usuarioNuevo || typeof usuarioNuevo !== 'object') return null;

      const usuarioCombinado = {
        ...(informacionRef.current || {}),
        ...usuarioNuevo,
      };

      informacionRef.current = usuarioCombinado;
      dispatch(setInformacion(usuarioCombinado));
      return usuarioCombinado;
    },
    [dispatch]
  );

  const actualizarInformacionUsuario = React.useCallback(async () => {
    try {
      const tokenJWT = getCookie('token');

      if (!tokenJWT) {
        console.log('[NavBar] ⚠️ No hay token para actualizar saldos.');
        return null;
      }

      const res = await axios.get(rutas.getInformacion, {
        headers: { token: tokenJWT },
      });

      const usuarioActualizado = extraerUsuarioRespuesta(res.data);

      if (usuarioActualizado) {
        console.log('[NavBar] ✅ Usuario actualizado desde getInformacion:', {
          nombre: usuarioActualizado?.nombre,
          dinero: usuarioActualizado?.dinero,
          dineroReal: usuarioActualizado?.dineroReal,
          fusion: usuarioActualizado?.fusion,
          energia: usuarioActualizado?.energia,
          nftUnicos: usuarioActualizado?.nftUnicos,
          totalNftsExistentes: usuarioActualizado?.totalNftsExistentes,
          porcentaje: usuarioActualizado?.porcentaje,
        });

        aplicarUsuarioLocal(usuarioActualizado);
        return usuarioActualizado;
      }

      console.log(
        '[NavBar] ⚠️ getInformacion respondió, pero no se pudo extraer usuario:',
        res.data
      );

      return null;
    } catch (error) {
      console.error(
        '[NavBar] Error actualizando información/saldos:',
        error?.response?.data || error?.message || error
      );

      return null;
    }
  }, [aplicarUsuarioLocal]);

  const aplicarEstadoDiario = React.useCallback((dailyPayload) => {
    if (!dailyPayload || typeof dailyPayload !== 'object') return;

    setWithdrawDaily((prev) => ({
      HUESO: {
        ...prev.HUESO,
        ...(dailyPayload.HUESO || {}),
      },
      VIPHUESO: {
        ...prev.VIPHUESO,
        ...(dailyPayload.VIPHUESO || {}),
      },
    }));
  }, []);

  const cargarEstadoRetiros = React.useCallback(async () => {
    const tokenJWT = getCookie('token');

    if (!tokenJWT || !rutas?.retiro) return null;

    setWithdrawStatusLoading(true);

    try {
      const response = await axios.get(rutas.retiro, {
        params: { action: 'status' },
        headers: {
          token: tokenJWT,
          'Content-Type': 'application/json',
        },
      });

      const usuarioActualizado = extraerUsuarioRespuesta(response?.data);

      if (usuarioActualizado) {
        aplicarUsuarioLocal(usuarioActualizado);
      }

      aplicarEstadoDiario(response?.data?.daily);
      return response?.data || null;
    } catch (statusError) {
      console.error(
        '[Withdraw status] Error:',
        statusError?.response?.data || statusError?.message || statusError
      );
      return null;
    } finally {
      setWithdrawStatusLoading(false);
    }
  }, [aplicarEstadoDiario, aplicarUsuarioLocal]);

  // =======================
  // Actualizar Poder Especial consumible
  // =======================
  const actualizarPoderEspecial = React.useCallback(async () => {
    if (poderEspecialRequestRef.current) return null;

    const tokenJWT = getCookie('token');
    const statusUrl = rutas?.arena?.status;

    if (!tokenJWT || !statusUrl) {
      setPoderEspecial((prev) => ({
        ...prev,
        loading: false,
        error: true,
      }));
      return null;
    }

    poderEspecialRequestRef.current = true;

    setPoderEspecial((prev) => ({
      ...prev,
      loading: !prev.loaded,
    }));

    try {
      const res = await axios.get(statusUrl, {
        headers: { token: tokenJWT },
      });

      const perfilArena = res?.data?.perfil || {};
      const original = Math.max(
        0,
        Number(
          perfilArena?.ppOriginal ??
            res?.data?.powerFromBurned?.total ??
            0
        ) || 0
      );
      const consumido = Math.max(
        0,
        Number(perfilArena?.ppQuema ?? 0) || 0
      );
      const activoCalculado = Math.max(0, original - consumido);
      const activo = Math.max(
        0,
        Number(perfilArena?.ppActivo ?? perfilArena?.pp ?? activoCalculado) || 0
      );

      const nuevoPoder = {
        activo,
        original,
        consumido,
        loading: false,
        loaded: true,
        error: false,
      };

      setPoderEspecial(nuevoPoder);
      return nuevoPoder;
    } catch (error) {
      console.error(
        '[NavBar] Error actualizando Poder Especial:',
        error?.response?.data || error?.message || error
      );

      // Se conserva el último valor válido para no mostrar 0 por un fallo temporal.
      setPoderEspecial((prev) => ({
        ...prev,
        loading: false,
        error: true,
      }));

      return null;
    } finally {
      poderEspecialRequestRef.current = false;
    }
  }, []);

  React.useEffect(() => {
    actualizarInformacionUsuario();
    actualizarPoderEspecial();

    const escucharActualizacion = (event) => {
      console.log('[NavBar] 🔄 Evento actualizar-saldos recibido.');

      // ✅ Actualización inmediata para que el saldo del header cambie sin recargar.
      // PetOverlay puede enviar detail.bdb con dinero, dineroReal, fusion y energia.
      const detalle = event?.detail;
      const usuarioActual = informacionRef.current;

      if (usuarioActual && detalle?.usuario && typeof detalle.usuario === 'object') {
        aplicarUsuarioLocal({
          ...usuarioActual,
          ...detalle.usuario,
        });
      }

      if (usuarioActual && detalle?.bdb && typeof detalle.bdb === 'object') {
        aplicarUsuarioLocal({
          ...usuarioActual,
          dinero:
            typeof detalle.bdb.dinero !== 'undefined'
              ? detalle.bdb.dinero
              : usuarioActual.dinero,
          dineroReal:
            typeof detalle.bdb.dineroReal !== 'undefined'
              ? detalle.bdb.dineroReal
              : usuarioActual.dineroReal,
          fusion:
            typeof detalle.bdb.fusion !== 'undefined'
              ? detalle.bdb.fusion
              : usuarioActual.fusion,
          energia:
            typeof detalle.bdb.energia !== 'undefined'
              ? detalle.bdb.energia
              : usuarioActual.energia,
        });
      }

      // Withdrawal responses already contain the new user. Avoid an immediate
      // request that could overwrite it with an older response.
      if (!detalle?.localOnly) {
        actualizarInformacionUsuario();
      }

      actualizarPoderEspecial();
    };

    const escucharActualizacionPoder = () => {
      actualizarPoderEspecial();
    };

    // ✅ Evento enviado por EcosystemEventWidget cuando el usuario retira
    // fragmentos del Golden Bone Hunt y recibe VIPHUESO.
    // En Big Dog Bone el VIPHUESO interno está guardado en el campo "fusion".
    // Esto actualiza el perfil y la barra superior al instante, sin recargar.
    const escucharRetiroFragmentosEcosystem = (event) => {
      const detalle = event?.detail || {};

      const esBigDogBone =
        detalle?.targetGame === 'bdb' ||
        detalle?.balanceField === 'fusion';

      if (!esBigDogBone) return;

      const nuevoSaldoVipHueso = Number(detalle?.afterBalance);

      if (!Number.isFinite(nuevoSaldoVipHueso)) return;

      const usuarioActual = informacionRef.current;

      if (usuarioActual) {
        dispatch(setInformacion({
          ...usuarioActual,
          fusion: nuevoSaldoVipHueso,
        }));
      }

      console.log('[NavBar] ✅ VipHueso actualizado por evento Ecosystem:', {
        beforeBalance: detalle?.beforeBalance,
        afterBalance: nuevoSaldoVipHueso,
        amount: detalle?.amount,
        operationId: detalle?.operationId,
      });

      // Confirmación en segundo plano contra el backend.
      actualizarInformacionUsuario();
    };

    const actualizarSiVuelveVisible = () => {
      if (document.visibilityState === 'visible') {
        console.log('[NavBar] 🔄 Pestaña visible otra vez. Actualizando saldos y PP.');
        actualizarInformacionUsuario();
        actualizarPoderEspecial();
      }
    };

    // Respaldo: confirma el PP cada 30 segundos mientras la pestaña esté visible.
    // Los eventos y el regreso a la pestaña siguen produciendo actualización inmediata.
    const intervaloPoder = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        actualizarPoderEspecial();
      }
    }, 30000);

    window.addEventListener('actualizar-saldos', escucharActualizacion);
    window.addEventListener(
      'actualizar-poder-especial',
      escucharActualizacionPoder
    );

    // ✅ Escucha directa del retiro de fragmentos del evento.
    window.addEventListener(
      'ecosystem:viphueso-balance-updated',
      escucharRetiroFragmentosEcosystem
    );

    // ✅ Compatibilidad con otros módulos que usen el evento genérico.
    window.addEventListener(
      'ecosystem:balance-updated',
      escucharRetiroFragmentosEcosystem
    );

    window.addEventListener('focus', escucharActualizacion);
    document.addEventListener('visibilitychange', actualizarSiVuelveVisible);

    return () => {
      window.clearInterval(intervaloPoder);
      window.removeEventListener('actualizar-saldos', escucharActualizacion);
      window.removeEventListener(
        'actualizar-poder-especial',
        escucharActualizacionPoder
      );
      window.removeEventListener(
        'ecosystem:viphueso-balance-updated',
        escucharRetiroFragmentosEcosystem
      );
      window.removeEventListener(
        'ecosystem:balance-updated',
        escucharRetiroFragmentosEcosystem
      );
      window.removeEventListener('focus', escucharActualizacion);
      document.removeEventListener('visibilitychange', actualizarSiVuelveVisible);
    };
  }, [
    actualizarInformacionUsuario,
    actualizarPoderEspecial,
    aplicarUsuarioLocal,
    dispatch,
  ]);

  // When the profile opens, refresh balances, power and daily limits.
  React.useEffect(() => {
    if (!open) return;

    actualizarPoderEspecial();
    cargarEstadoRetiros();
  }, [open, actualizarPoderEspecial, cargarEstadoRetiros]);

  // =======================
  // Helpers perfil
  // =======================
  const bonus = (informacion?.nivel ?? 0) * 0.25;
  const missionsDone = informacion?.cantidadMision ?? 0;
  const missionsCap = 600;

  // =======================
  // Colección NFT visible
  // =======================
  // Si el backend envía totalNftsExistentes se usa directamente.
  // Mientras esa propiedad no llegue, se obtiene el total usando
  // nftUnicos y porcentaje, valores que ya llegan correctamente.
  // Ejemplo actual: 25 / 18.66% = 134 NFT existentes.
  const nftsUnicosMostrar = Number(informacion?.nftUnicos ?? 0);
  const porcentajeNftsMostrar = Number(informacion?.porcentaje ?? 0);
  const totalNftsDesdeBackend = Number(informacion?.totalNftsExistentes ?? 0);

  const totalNftsMostrar =
    totalNftsDesdeBackend > 0
      ? totalNftsDesdeBackend
      : nftsUnicosMostrar > 0 && porcentajeNftsMostrar > 0
        ? Math.round(nftsUnicosMostrar / (porcentajeNftsMostrar / 100))
        : 0;

  const missionsPct = Math.min(
    100,
    missionsCap > 0 ? (missionsDone / missionsCap) * 100 : 0
  );

  const formatoNumero = (valor) => {
    const numero = Number(valor || 0);

    return numero.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 8,
    });
  };

  const getWithdrawBalance = (token) => {
    const rule = WITHDRAW_RULES[token];
    return Number(informacion?.[rule?.balanceField] || 0);
  };

  const getWithdrawMaximum = (token) => {
    const balance = getWithdrawBalance(token);
    const dailyRemaining = Number(
      withdrawDaily?.[token]?.remaining ?? WITHDRAW_RULES[token].dailyLimit
    );

    return Math.max(0, Math.min(balance, dailyRemaining));
  };

  const setWithdrawAmount = (token, value) => {
    const cleanValue = String(value ?? '').replace(',', '.');

    setWithdrawAmounts((prev) => ({
      ...prev,
      [token]: cleanValue,
    }));
  };

  const usarMaximoRetiro = (token) => {
    const maximum = getWithdrawMaximum(token);
    setWithdrawAmount(token, maximum > 0 ? String(maximum) : '');
  };

  // =======================
  // Handlers generales
  // =======================
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const handleClose2 = () => setOpen2(false);

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const cerrarSesion = () => {
    /*
     * 1. Limpia Redux.
     * 2. Elimina la sesión guardada.
     * 3. Elimina cualquier token local.
     * 4. Elimina la cookie JWT.
     * 5. Recarga directamente el nuevo login para impedir
     *    que el botón Atrás vuelva a mostrar el juego.
     */
    dispatch(exit({}));

    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');

    localStorage.removeItem('user');
    localStorage.removeItem('token');

    document.cookie =
      'token=; expires=Thu, 01 Jan 1970 00:00:00 GMT; Max-Age=0; path=/; SameSite=Strict; Secure';

    window.location.replace('/ecosystem');
  };

  const handleChange = (e) => setPassword(e.target.value);

  // =======================
  // Transferencias HIVE-ENGINE
  // =======================
  const transfer = (cantidad, onSuccess, memo = 'Deposit', token = 'HUESO') => {
    const cantidadNumero = Number(cantidad);

    if (!Number.isFinite(cantidadNumero) || cantidadNumero <= 0) {
      setErrorText('Invalid deposit amount');
      setError(true);
      setOpen2(true);
      return;
    }

    if (!window.hive_keychain) {
      setErrorText('Hive Keychain is not available');
      setError(true);
      setOpen2(true);
      return;
    }

    const username = JSON.parse(sessionStorage.getItem('user') || '{}');

    if (!username?.user) {
      setErrorText('User session not found');
      setError(true);
      setOpen2(true);
      return;
    }

    const json = {
      contractName: 'tokens',
      contractAction: 'transfer',
      contractPayload: {
        symbol: token,
        to: 'mv-token1',
        quantity: cantidadNumero.toString(),
        memo: memo,
      },
    };

    window.hive_keychain.requestCustomJson(
      username.user,
      'ssc-mainnet-hive',
      'Active',
      JSON.stringify(json),
      'Transfer',
      async function (response) {
        if (response.success && response.result) {
          try {
            const tokenJWT = getCookie('token');

            const resultado = await axios.post(
              rutas.deposit,
              {
                id: response?.result?.id,
              },
              {
                headers: { token: tokenJWT },
              }
            );

            if (resultado.status === 200) {
              const usuarioActualizado = extraerUsuarioRespuesta(resultado.data);

              if (usuarioActualizado) {
                dispatch(setInformacion(usuarioActualizado));
              } else {
                await actualizarInformacionUsuario();
              }

              window.dispatchEvent(new Event('actualizar-saldos'));

              setError(false);
              setErrorText('');
              setDeposit(0);

              if (typeof onSuccess === 'function') {
                onSuccess();
              }
            }
          } catch (error) {
            console.error(
              '[Deposit] Error:',
              error?.response?.data || error?.message || error
            );

            setErrorText(
              error?.response?.data?.error || 'Deposit could not be completed'
            );
            setError(true);
            setOpen2(true);

            await actualizarInformacionUsuario();
          }
        } else {
          console.log(response);

          setErrorText(response?.message || 'Transaction rejected or cancelled');
          setError(true);
          setOpen2(true);
        }
      }
    );
  };

  const handleTransfer = (token = 'HUESO') => {
    transfer(deposit, () => setOpen2(true), 'Deposit', token);
  };

  // =======================
  // Withdrawals
  // =======================
  const retiroInterno = async () => {
    try {
      const tokenJWT = getCookie('token');

      if (!tokenJWT) {
        setErrorText('Your session has expired. Please log in again.');
        setError(true);
        setOpen2(true);
        return;
      }

      const resultado = await axios({
        method: 'get',
        url: rutas.retiroInterno,
        headers: {
          token: tokenJWT,
          'Content-Type': 'application/json',
        },
      });

      if (!resultado?.data) {
        throw new Error('The internal withdrawal returned no data.');
      }

      const usuarioActualizado = extraerUsuarioRespuesta(resultado.data);

      if (usuarioActualizado) {
        dispatch(setInformacion(usuarioActualizado));
      } else {
        await actualizarInformacionUsuario();
      }

      window.dispatchEvent(
        new CustomEvent('actualizar-saldos', {
          detail: usuarioActualizado ? { usuario: usuarioActualizado } : {},
        })
      );

      setError(false);
      setErrorText('');
      setSuccessText(
        resultado?.data?.message ||
          'Your Mini-HUESO balance was moved successfully.'
      );
      setOpen2(true);
    } catch (error) {
      setErrorText(
        error?.response?.data?.error ||
          error?.message ||
          'The internal withdrawal could not be completed.'
      );
      setError(true);
      setOpen2(true);

      await actualizarInformacionUsuario();
    }
  };

  const retirarToken = async (token) => {
    const tokenNormalizado = String(token || '').trim().toUpperCase();

    if (!['HUESO', 'VIPHUESO'].includes(tokenNormalizado)) {
      setErrorText('Invalid withdrawal token.');
      setError(true);
      setOpen2(true);
      return;
    }

    if (withdrawLoading) return;

    const rule = WITHDRAW_RULES[tokenNormalizado];
    const amount = Number(withdrawAmounts[tokenNormalizado]);
    const availableBalance = getWithdrawBalance(tokenNormalizado);
    const dailyRemaining = Number(
      withdrawDaily?.[tokenNormalizado]?.remaining ?? rule.dailyLimit
    );

    if (!Number.isFinite(amount) || amount <= 0) {
      setErrorText(`Enter the amount of ${tokenNormalizado} you want to withdraw.`);
      setError(true);
      setOpen2(true);
      return;
    }

    if (amount < rule.minimum) {
      setErrorText(
        `The minimum withdrawal is ${formatoNumero(rule.minimum)} ${tokenNormalizado}.`
      );
      setError(true);
      setOpen2(true);
      return;
    }

    if (amount > availableBalance) {
      setErrorText(
        `Insufficient ${tokenNormalizado} balance. Available: ${formatoNumero(
          availableBalance
        )} ${tokenNormalizado}.`
      );
      setError(true);
      setOpen2(true);
      return;
    }

    if (amount > dailyRemaining) {
      setErrorText(
        `You can withdraw up to ${formatoNumero(
          dailyRemaining
        )} ${tokenNormalizado} before 00:00 UTC.`
      );
      setError(true);
      setOpen2(true);
      return;
    }

    try {
      const tokenJWT = getCookie('token');

      if (!tokenJWT) {
        setErrorText('Your session has expired. Please log in again.');
        setError(true);
        setOpen2(true);
        return;
      }

      setWithdrawLoading(tokenNormalizado);

      const resultado = await axios({
        method: 'get',
        url: rutas.retiro,
        params: {
          token: tokenNormalizado,
          amount,
        },
        headers: {
          token: tokenJWT,
          'Content-Type': 'application/json',
        },
      });

      if (!resultado?.data) {
        throw new Error('The withdrawal returned no data.');
      }

      const usuarioActualizado = extraerUsuarioRespuesta(resultado.data);
      let usuarioAplicado = null;

      if (usuarioActualizado) {
        usuarioAplicado = aplicarUsuarioLocal(usuarioActualizado);
      } else {
        usuarioAplicado = await actualizarInformacionUsuario();
      }

      if (resultado?.data?.daily) {
        aplicarEstadoDiario({
          [tokenNormalizado]: resultado.data.daily,
        });
      }

      setWithdrawAmounts((prev) => ({
        ...prev,
        [tokenNormalizado]: '',
      }));

      window.dispatchEvent(
        new CustomEvent('actualizar-saldos', {
          detail: {
            usuario: usuarioAplicado || usuarioActualizado || undefined,
            bdb: usuarioAplicado
              ? {
                  dinero: usuarioAplicado.dinero,
                  dineroReal: usuarioAplicado.dineroReal,
                  fusion: usuarioAplicado.fusion,
                  energia: usuarioAplicado.energia,
                }
              : undefined,
            localOnly: true,
          },
        })
      );

      // Confirm shortly after, without delaying the visible balance update.
      window.setTimeout(() => {
        actualizarInformacionUsuario();
        cargarEstadoRetiros();
      }, 1200);

      setError(false);
      setErrorText('');
      setSuccessText(
        resultado?.data?.message ||
          `${formatoNumero(amount)} ${tokenNormalizado} were sent to your Hive wallet.`
      );
      setOpen2(true);
    } catch (withdrawError) {
      console.error(
        `[Withdraw ${tokenNormalizado}] Error:`,
        withdrawError?.response?.data ||
          withdrawError?.message ||
          withdrawError
      );

      const dailyFromError = withdrawError?.response?.data?.daily;

      if (dailyFromError) {
        aplicarEstadoDiario({
          [tokenNormalizado]: dailyFromError,
        });
      }

      setErrorText(
        withdrawError?.response?.data?.error ||
          withdrawError?.message ||
          `${tokenNormalizado} withdrawal failed.`
      );
      setError(true);
      setOpen2(true);

      await actualizarInformacionUsuario();
      await cargarEstadoRetiros();
    } finally {
      setWithdrawLoading('');
    }
  };

  // =======================
  // Cambiar password
  // =======================
  const changePassword = async () => {
    try {
      if (password === '' || password.length < 4 || password.length > 12) {
        setErrorText('Invalid password');
        setError(true);
        setOpen2(true);
        return;
      }

      const tokenJWT = getCookie('token');

      const resultado = await axios({
        method: 'post',
        url: rutas.changePass,
        headers: {
          token: tokenJWT,
          'Content-Type': 'application/json',
        },
        data: { password },
      });

      if (resultado?.data) {
        setPassword('');
        setError(false);
        setErrorText('');
        setOpen2(true);
      }
    } catch (error) {
      setErrorText(error?.response?.data?.error || 'Could not change password');
      setError(true);
      setOpen2(true);
    }
  };

  // =======================
  // Render
  // =======================
  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: '#202020',
        borderBottom: '1px solid rgba(255,193,7,0.18)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.32)',
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 1.5, md: 2 } }}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 58, md: 64 },
            gap: { xs: 0.75, md: 1 },
            flexWrap: 'nowrap',
          }}
        >
          {/* MENÚ MÓVIL */}
          <Box
            sx={{
              flexGrow: { xs: 1, md: 0 },
              display: { xs: 'flex', md: 'none' },
              justifyContent: 'flex-start',
            }}
          >
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              <MenuItem onClick={handleCloseNavMenu}>
                <Link
                  style={{ color: 'white', textDecoration: 'none' }}
                  to="/game/"
                >
                  HOME
                </Link>
              </MenuItem>

              <MenuItem onClick={handleCloseNavMenu}>
                <Link
                  style={{ color: 'white', textDecoration: 'none' }}
                  to="/game/album"
                >
                  ALBUM
                </Link>
              </MenuItem>

              <MenuItem onClick={handleCloseNavMenu}>
                <Link
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    textTransform: 'uppercase',
                  }}
                  to="/game/market"
                >
                  in-game market
                </Link>
              </MenuItem>

              <MenuItem onClick={handleCloseNavMenu}>
                <Link
                  style={{ color: 'white', textDecoration: 'none' }}
                  to="/game/inventory"
                >
                  INVENTORY
                </Link>
              </MenuItem>

              <MenuItem onClick={handleCloseNavMenu}>
                <Link
                  style={{ color: 'white', textDecoration: 'none' }}
                  to="/game/stake"
                >
                  STAKE
                </Link>
              </MenuItem>
            </Menu>
          </Box>

          {/* LOGO */}
          <CardMedia
            component="img"
            image={logo}
            alt="avatar"
            sx={{
              width: { md: 78, lg: 88 },
              mr: { md: 0.5, lg: 1 },
              flexShrink: 0,
              display: { xs: 'none', md: 'block' },
            }}
          />

          {/* MENÚ DESKTOP */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { md: 0.75, lg: 1.4 },
              mx: { md: 0.5, lg: 1 },
            }}
          >
            {[
              { label: 'HOME', to: '/game/' },
              { label: 'IN-GAME MARKET', to: '/game/market' },
              { label: 'ALBUM', to: '/game/album' },
              { label: 'INVENTORY', to: '/game/inventory' },
              { label: 'STAKE', to: '/game/stake' },
            ].map((item) => (
              <Button
                key={item.to}
                component={Link}
                to={item.to}
                variant="contained"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  maxWidth: item.label === 'IN-GAME MARKET' ? 172 : 150,
                  height: 38,
                  px: { md: 0.8, lg: 1.6 },
                  bgcolor: '#ff9800',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: { md: '0.7rem', lg: '0.78rem' },
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
                  '&:hover': {
                    bgcolor: '#ffad33',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* INFO DERECHA (Hueso, energía, iconos) */}
          <Box
            sx={{
              width: { xs: 'auto', md: 'auto' },
              minWidth: 0,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: { xs: 0.25, sm: 0.45, lg: 0.65 },
              whiteSpace: 'nowrap',
            }}
          >
            <Tooltip
              arrow
              placement="bottom"
              title={
                poderEspecial.loaded
                  ? `Special Power: ${formatoNumero(
                      poderEspecial.activo
                    )} / ${formatoNumero(
                      poderEspecial.original
                    )}. Click to burn NFTs and get more.`
                  : 'Loading Special Power...'
              }
            >
              <Box
                component={Link}
                to={BURN_PATH}
                aria-label="Open NFT burn system to get more Special Power"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.45,
                  minWidth: { xs: 72, sm: 86, lg: 96 },
                  px: { xs: 0.7, sm: 0.9 },
                  py: 0.55,
                  mr: { xs: 0.2, md: 0.35 },
                  borderRadius: 1.6,
                  border: '1px solid rgba(186,104,200,0.9)',
                  background:
                    'linear-gradient(135deg, rgba(123,31,162,0.34), rgba(255,193,7,0.16))',
                  color: '#f3d9ff',
                  textDecoration: 'none',
                  boxShadow: '0 0 10px rgba(186,104,200,0.38)',
                  flexShrink: 0,
                  cursor: 'pointer',
                  transition:
                    'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease, background 0.18s ease',
                  '&:hover': {
                    transform: 'translateY(-2px) scale(1.03)',
                    borderColor: '#ffd54f',
                    background:
                      'linear-gradient(135deg, rgba(156,39,176,0.48), rgba(255,193,7,0.26))',
                    boxShadow:
                      '0 0 18px rgba(255,213,79,0.65), 0 0 12px rgba(186,104,200,0.55)',
                  },
                  '&:active': {
                    transform: 'scale(0.97)',
                  },
                }}
              >
                <Typography
                  component="span"
                  sx={{ fontSize: { xs: '0.72rem', md: '0.88rem' }, lineHeight: 1 }}
                >
                  ✨
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    color: '#f3d9ff',
                    fontWeight: 900,
                    fontSize: { xs: '0.7rem', sm: '0.76rem', lg: '0.84rem' },
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {poderEspecial.loading && !poderEspecial.loaded
                    ? '...'
                    : formatoNumero(poderEspecial.activo)}
                </Typography>
              </Box>
            </Tooltip>

            <Typography
              sx={{
                color: 'gold',
                fontWeight: 900,
                fontSize: { xs: '0.72rem', sm: '0.82rem', lg: '0.9rem' },
                lineHeight: 1,
              }}
            >
              {formatoNumero(informacion?.dinero)}
            </Typography>

            <CardMedia
              component="img"
              sx={{
                width: { xs: 16, md: 18, lg: 20 },
                mr: { xs: 0.2, md: 0.4 },
                flexShrink: 0,
              }}
              image={hueso}
              alt="Hueso"
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 0.15, lg: 0.25 },
                whiteSpace: 'nowrap',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.68rem', sm: '0.78rem', lg: '0.86rem' },
                  lineHeight: 1,
                }}
              >
                {informacion?.energia?.actual ?? 0} / {informacion?.energia?.maximo ?? 0}
              </Typography>

              <EvStationIcon sx={{ fontSize: { xs: 27, md: 30, lg: 34 } }} />

              <AccountCircleIcon
                onClick={handleOpen}
                sx={{
                  fontSize: { xs: 27, md: 30, lg: 34 },
                  cursor: 'pointer',
                  transition: 'transform 0.18s ease, color 0.18s ease',
                  '&:hover': { color: '#ffd54f', transform: 'scale(1.08)' },
                }}
              />

              <ExitToAppIcon
                onClick={cerrarSesion}
                role="button"
                tabIndex={0}
                aria-label="Log out"
                titleAccess="Log out"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    cerrarSesion();
                  }
                }}
                sx={{
                  fontSize: { xs: 27, md: 30, lg: 34 },
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease, color 0.2s ease',
                  '&:hover': {
                    color: '#ff9800',
                    transform: 'scale(1.1)',
                  },
                }}
              />

              <a
                href="https://mundo-virtual.gitbook.io/whitepaper/"
                target="_blank"
                rel="noreferrer"
              >
                <BookIcon
                  sx={{
                    fontSize: { xs: 27, md: 30, lg: 34 },
                    '&:hover': { cursor: 'pointer', fontStyle: 'none' },
                  }}
                />
              </a>

              <a
                href="https://discord.gg/GJS6MtKB2x"
                target="_blank"
                rel="noreferrer"
              >
                <CardMedia
                  component="img"
                  image={discord}
                  alt="discord"
                  sx={{
                    width: { xs: '1.4rem', md: '1.7rem' },
                    height: {
                      xs: '1.4rem',
                      md: '1.7rem',
                      '&:hover': { cursor: 'pointer' },
                    },
                  }}
                />
              </a>
            </Box>
          </Box>
        </Toolbar>
      </Container>

      {/* ======================= MODAL PERFIL ======================= */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/* HEADER */}
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <Avatar
              sx={{
                bgcolor: 'goldenrod',
                width: 44,
                height: 44,
                fontWeight: 'bold',
                fontSize: 20,
                boxShadow: '0 0 14px rgba(255,215,0,0.9)',
              }}
            >
              {informacion?.nombre?.charAt(0)?.toUpperCase() || '?'}
            </Avatar>

            <Box sx={{ flexGrow: 1 }}>
              <Typography
                id="modal-modal-title"
                variant="h6"
                sx={{ fontWeight: 'bold', letterSpacing: 1 }}
              >
                {informacion?.nombre || 'Player'}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 0.3, flexWrap: 'wrap' }}
              >
                <Chip
                  size="small"
                  label={`Level ${informacion?.nivel ?? 0}`}
                  sx={{
                    bgcolor: 'rgba(255,165,0,0.15)',
                    border: '1px solid rgba(255,165,0,0.7)',
                    color: 'orange',
                  }}
                />

                <Chip
                  size="small"
                  label={`Bonus ${bonus.toFixed(2)}%`}
                  sx={{
                    bgcolor: 'rgba(50,205,50,0.15)',
                    border: '1px solid rgba(50,205,50,0.8)',
                    color: 'lime',
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* ESTADÍSTICAS EN 2 COLUMNAS */}
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            {/* Columna izquierda */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 0.6, color: 'gold', fontWeight: 'bold' }}
                >
                  Player Stats
                </Typography>

                <Typography variant="body2">
                  <strong>Unique NFT:</strong>{' '}
                  {nftsUnicosMostrar} / {totalNftsMostrar}
                </Typography>

                <Typography variant="body2">
                  <strong>Percentage:</strong>{' '}
                  {porcentajeNftsMostrar.toFixed(2)}%
                </Typography>

                <Typography variant="body2" sx={{ mt: 0.6 }}>
                  <strong>Missions done:</strong> {missionsDone} / {missionsCap}{' '}
                  free before Power Collection
                </Typography>

                <Box
                  sx={{
                    mt: 0.5,
                    height: 6,
                    borderRadius: 999,
                    overflow: 'hidden',
                    bgcolor: 'rgba(255,255,255,0.06)',
                  }}
                >
                  <Box
                    sx={{
                      width: `${missionsPct}%`,
                      height: '100%',
                      background:
                        'linear-gradient(90deg, #FFD700, #FF9800, #FF5722)',
                    }}
                  />
                </Box>
              </Box>
            </Grid>

            {/* Columna derecha */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 1.2,
                  borderRadius: 2,
                  bgcolor: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ mb: 0.6, color: 'gold', fontWeight: 'bold' }}
                >
                  Power & Balances
                </Typography>

                <Tooltip
                  arrow
                  placement="top"
                  title="Click to open the NFT burn system and get more Special Power."
                >
                  <Box
                    component={Link}
                    to={BURN_PATH}
                    onClick={handleClose}
                    sx={{
                      display: 'block',
                      mb: 0.7,
                      p: 0.85,
                      borderRadius: 1.5,
                      border: '1px solid rgba(186,104,200,0.55)',
                      background:
                        'linear-gradient(135deg, rgba(123,31,162,0.22), rgba(255,193,7,0.08))',
                      color: 'inherit',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition:
                        'transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        borderColor: '#ffd54f',
                        boxShadow: '0 0 14px rgba(186,104,200,0.35)',
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography
                        variant="body2"
                        sx={{ color: '#e1bee7', fontWeight: 800 }}
                      >
                        ✨ Special Power
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{ color: '#ffd54f', fontWeight: 800 }}
                      >
                        Burn NFTs →
                      </Typography>
                    </Stack>

                    <Typography
                      variant="body2"
                      sx={{ mt: 0.35, color: '#fff', fontWeight: 800 }}
                    >
                      {poderEspecial.loading && !poderEspecial.loaded
                        ? 'Loading...'
                        : `${formatoNumero(
                            poderEspecial.activo
                          )} / ${formatoNumero(poderEspecial.original)}`}
                    </Typography>

                    <Box
                      sx={{
                        mt: 0.55,
                        height: 6,
                        borderRadius: 999,
                        overflow: 'hidden',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      }}
                    >
                      <Box
                        sx={{
                          width: `${
                            poderEspecial.original > 0
                              ? Math.min(
                                  100,
                                  (poderEspecial.activo /
                                    poderEspecial.original) *
                                    100
                                )
                              : 0
                          }%`,
                          height: '100%',
                          background:
                            'linear-gradient(90deg, #7b1fa2, #ba68c8, #ffd54f)',
                          transition: 'width 0.35s ease',
                        }}
                      />
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 0.35, opacity: 0.72 }}
                    >
                      Used: {formatoNumero(poderEspecial.consumido)} PP
                      {poderEspecial.error ? ' · reconnecting…' : ''}
                    </Typography>
                  </Box>
                </Tooltip>

                <Typography variant="body2">
                  <strong>Power Hueso:</strong> {formatoNumero(informacion?.ph)}
                </Typography>

                <Typography variant="body2">
                  <strong>Power energy:</strong> {formatoNumero(informacion?.pe)}
                </Typography>

                <Typography variant="body2">
                  <strong>Mini-HUESO:</strong> {formatoNumero(informacion?.dinero)}
                </Typography>

                <Typography variant="body2">
                  <strong>HUESO In-Game:</strong>{' '}
                  {formatoNumero(informacion?.dineroReal)}
                </Typography>

                <Typography variant="body2">
                  <strong>VIPHUESO:</strong> {formatoNumero(informacion?.fusion)}
                </Typography>

                <Typography variant="body2">
                  <strong>Energy:</strong>{' '}
                  {informacion?.energia?.actual ?? 0} / {informacion?.energia?.maximo ?? 0}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mt: 0.4, color: 'lime', fontWeight: 'bold' }}
                >
                  Total Bonus: {bonus.toFixed(2)}%
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* CUENTA / PASSWORD */}
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 'bold' }}>
            Account & Security
          </Typography>

          <TextField
            value={password}
            onChange={handleChange}
            type="password"
            fullWidth
            label="New password"
            variant="standard"
            sx={{ mb: 1.5 }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Button
              disabled={!informacion?.packInicial}
              onClick={retiroInterno}
              variant="contained"
              fullWidth
            >
              {informacion?.packInicial
                ? 'Move Mini-HUESO to HUESO In-Game'
                : 'Buy Initial Pack'}
            </Button>

            <Button onClick={changePassword} variant="outlined" fullWidth>
              Change Password
            </Button>
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* DEPOSITS */}
          <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 'bold' }}>
            Deposits
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 1 }}
          >
            <TextField
              value={deposit}
              onChange={(e) => setDeposit(e.target.value)}
              type="number"
              fullWidth
              label="Amount to deposit"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Button
              onClick={() => handleTransfer('HUESO')}
              variant="contained"
              fullWidth
            >
              Deposit HUESO
            </Button>

            <Button
              onClick={() => handleTransfer('VIPHUESO')}
              variant="contained"
              color="secondary"
              fullWidth
            >
              Deposit VIPHUESO
            </Button>
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* WITHDRAWALS */}
          <Typography variant="body2" sx={{ mb: 0.45, fontWeight: 'bold' }}>
            Withdrawals to Hive Wallet
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 1, color: 'rgba(255,255,255,0.68)' }}
          >
            Choose the amount to withdraw. Daily limits reset at 00:00 UTC:
            50,000 HUESO and 200 VIPHUESO.
          </Typography>

          <Stack spacing={1.1} sx={{ mb: 1.5 }}>
            {['HUESO', 'VIPHUESO'].map((token) => {
              const rule = WITHDRAW_RULES[token];
              const balance = getWithdrawBalance(token);
              const daily = withdrawDaily[token] || {};
              const amount = Number(withdrawAmounts[token]);
              const validAmount =
                Number.isFinite(amount) &&
                amount >= rule.minimum &&
                amount <= balance &&
                amount <= Number(daily.remaining ?? rule.dailyLimit);
              const isLoading = withdrawLoading === token;

              return (
                <Box
                  key={token}
                  sx={{
                    p: 1,
                    borderRadius: 1.8,
                    border:
                      token === 'VIPHUESO'
                        ? '1px solid rgba(206,147,216,0.55)'
                        : '1px solid rgba(255,183,77,0.55)',
                    bgcolor:
                      token === 'VIPHUESO'
                        ? 'rgba(156,39,176,0.08)'
                        : 'rgba(255,152,0,0.07)',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    spacing={0.35}
                    sx={{ mb: 0.75 }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 900 }}>
                      {token}
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.78)' }}
                    >
                      Balance: {formatoNumero(balance)} · Daily remaining:{' '}
                      {withdrawStatusLoading
                        ? '...'
                        : formatoNumero(daily.remaining ?? rule.dailyLimit)}
                    </Typography>
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8}>
                    <TextField
                      value={withdrawAmounts[token]}
                      onChange={(event) =>
                        setWithdrawAmount(token, event.target.value)
                      }
                      type="number"
                      fullWidth
                      size="small"
                      label={`Amount of ${token}`}
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: rule.minimum,
                        max: getWithdrawMaximum(token),
                        step: rule.step,
                      }}
                    />

                    <Button
                      onClick={() => usarMaximoRetiro(token)}
                      disabled={
                        Boolean(withdrawLoading) ||
                        getWithdrawMaximum(token) < rule.minimum
                      }
                      variant="outlined"
                      sx={{ minWidth: { sm: 76 } }}
                    >
                      Max
                    </Button>

                    <Button
                      onClick={() => retirarToken(token)}
                      disabled={Boolean(withdrawLoading) || !validAmount}
                      variant="contained"
                      color={token === 'VIPHUESO' ? 'secondary' : 'warning'}
                      sx={{ minWidth: { sm: 155 } }}
                    >
                      {isLoading ? `Sending ${token}...` : `Withdraw ${token}`}
                    </Button>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.55, opacity: 0.66 }}
                  >
                    Minimum: {formatoNumero(rule.minimum)} {token} · Used today:{' '}
                    {formatoNumero(daily.used || 0)} /{' '}
                    {formatoNumero(daily.limit || rule.dailyLimit)}
                  </Typography>
                </Box>
              );
            })}
          </Stack>

          <Divider sx={{ my: 1.5, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* REFERRAL */}
          <Typography variant="body2" sx={{ mb: 0.4, fontWeight: 'bold' }}>
            Referral link
          </Typography>

          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-all',
              color: 'rgba(255,255,255,0.8)',
            }}
          >
            {`https://www.bigdogbonegame.com/ecosystem/login/${informacion?.nombre}`}
          </Typography>
        </Box>
      </Modal>

      {/* SNACKBAR GLOBAL */}
      <Stack spacing={2} sx={{ width: '100%' }}>
        <Snackbar open={open2} autoHideDuration={6000} onClose={handleClose2}>
          {error ? (
            <Alert
              onClose={handleClose2}
              severity="error"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {errorText}
            </Alert>
          ) : (
            <Alert
              onClose={handleClose2}
              severity="success"
              variant="filled"
              sx={{ width: '100%' }}
            >
              {successText}
            </Alert>
          )}
        </Snackbar>
      </Stack>
    </AppBar>
  );
};

export default NavBar;